import {Injectable, NgZone} from '@angular/core';
import { hymnsDetailed } from "../hymn/hymnsDetailed";
import {Router} from "@angular/router";
import {MidiPlayerService} from "./midi-player.service";
import {HymnSelectionService} from "./hymn-selection.service";
import {LayoutService} from "./layout.service";
import {SidenavService} from "./sidenav.service";
import {SettingsService} from "./settings.service";
import {AutoScrollService} from "./auto-scroll.service";
import {PresentationAutoScrollService} from "./presentation-auto-scroll.service";
import {translation} from "../translation";

@Injectable({
  providedIn: 'root'
})
export class ContinuousPlaybackService {

  playlist: any[] = [];
  hymnEndListener: any;
  active = false;
  private sessionView: 'reader' | 'presenter' = 'reader';

  constructor(private router: Router,
              private midiPlayerService: MidiPlayerService,
              private hymnSelectionService: HymnSelectionService,
              private layoutService: LayoutService,
              private sideNavService: SidenavService,
              private settingsService: SettingsService,
              private zone: NgZone,
              private autoScrollService: AutoScrollService,
              private presentationAutoScrollService: PresentationAutoScrollService) {
  }

  async shuffle(array: string[]) {
    let currentIndex = array.length;

    // While there remain elements to shuffle...
    while (currentIndex != 0) {

      // Pick a remaining element...
      let randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]];
    }

    return array;
  }

  index = 0;
  async startContinuousPlayback(favoritesOnly: boolean = false) {
    if(favoritesOnly) {
      this.playlist = this.settingsService.getFavorites().filter(hymn => hymn.hasMidi);
    } else {
      this.playlist = hymnsDetailed.filter(hymn => hymn.hasMidi);
    }

    await this.shuffle(this.playlist);
    console.debug('Starting continuous playback');

    // Decide session view once per session: use presentation if currently selected, otherwise reader
    const mode = this.settingsService.getHymnViewMode();
    this.sessionView = (mode === this.settingsService.PRESENTATION) ? 'presenter' : 'reader';

    // Do not overwrite the user's hymnViewMode setting

    // Prepare autoscroll according to target view (session defaults to Karaoke for reader, Manual for presenter)
    this.active = true;
    if (this.sessionView === 'reader') {
      // Reader: default to autoscroll and force-enable autoscroll during continuous playback
      // Note: if user selects karaoke and it's available, it should probably stay karaoke,
      // but the initial requirement said default autoscroll.
      this.autoScrollService.setMode('autoscroll');
      this.autoScrollService.forceOn();
    } else {
      // Presenter: default to manual for the session (as requested)
      this.presentationAutoScrollService.setMode('manual');
    }

    this.index = 0;

    // --- NEW UNIFIED EVENT STREAM SUBSCRIPTION ---
    this.hymnEndListener = this.midiPlayerService.event$.subscribe(ev => {
      this.zone.run(() => {
        if (this.active && ev?.type === 'status' && ev.status === 'ended') {
          this.index++;
          if (this.index >= this.playlist.length) {
            this.index = 0;
          }
          this.playHymn(this.index, 2000);
        }
      });
    });

    this.playHymn(this.index);
    this.midiPlayerService.setPreviousButtonAction(() => this.previous());
    this.midiPlayerService.setNextButtonAction(() => this.next());
  }

  async stopContinuousPlayback() {
    this.zone.run(() => {
      // Stop autoscroll services used during continuous playback
      if (this.sessionView === 'reader') {
        this.autoScrollService.stopAutoScroll();
        this.autoScrollService.forceOff();
        // Restore autoscroll enabled state according to the user's setting (do not change the setting)
        const readerMode = this.settingsService.getAutoscroll();
        if (readerMode !== 'off') {
          this.autoScrollService.enableAutoScroll();
        } else {
          this.autoScrollService.disableAutoScroll();
        }
      } else {
        // Presenter session
        this.presentationAutoScrollService.stopAutoScroll();
      }

      if (this.hymnEndListener) {
        this.hymnEndListener.unsubscribe();
      }
      this.active = false;
      this.midiPlayerService.stop();
      this.midiPlayerService.clearPreviousButtonAction();
      this.midiPlayerService.clearNextButtonAction();
    });
  }

  startTimeout: any;

  async playHymn(index: number, waitDuration: number = 2000) {
    const playing: boolean = this.startTimeout || this.midiPlayerService.playing;
    clearTimeout(this.startTimeout);

    this.midiPlayerService.stop();

    console.debug('Playing ' + this.playlist[index]);

    // Navigate to the correct component based on session view
    if (this.sessionView === 'presenter') {
      await this.router.navigate([`/${translation.path_present}`, this.playlist[index].hymnNumber]);
    } else {
      await this.router.navigate([`/${translation.path_hymn}`, this.playlist[index].hymnNumber]);
    }
    this.sideNavService.closeList();

    // Reset scroll position for reader mode
    if (this.sessionView === 'reader') {
      this.autoScrollService.scrollToTop();
    }

    this.startTimeout = setTimeout(() => {
      // Start appropriate autoscroll service for this session view (preserve current session mode)
      if (this.sessionView === 'reader') {
        // Mode was set to 'karaoke' at session start (or changed live via UI). Just start it.
        this.autoScrollService.startAutoScroll();
      } else {
        // Presentation: if not manual, ensure it's running. Do not change current mode here.
        if (this.presentationAutoScrollService.mode !== 'manual') {
          this.presentationAutoScrollService.startAutoScroll();
        } else {
          this.presentationAutoScrollService.stopAutoScroll();
        }
      }

      this.midiPlayerService.playMidi(
        this.playlist[index],
        this.settingsService.getInstruments(),
        this.playlist[index].numberOfVerses,
        this.midiPlayerService.playbackSpeed
      );
    }, playing ? waitDuration : 0);
  }

  next() {
    this.index++;
    if (this.index >= this.playlist.length) {
      this.index = 0;
    }
    this.playHymn(this.index);
  }

  previous() {
    this.index--;
    if (this.index < 0) {
      this.index = this.playlist.length - 1;
    }
    this.playHymn(this.index);
  }
}
